// Ubicación en el repo: src/components/BranchFormModal.jsx
// Modal Crear/Editar sucursal (spec-prototipo.md 1.9). Mismo componente
// para ambos casos, igual patrón que PatientFormModal/AppointmentFormModal.
// Incluye el logo de la sucursal (DECISIONS.md #39) — se sube recién
// después de guardar nombre/dirección/teléfono, porque la ruta de Storage
// necesita el id de la sucursal (para una nueva, el que devuelve createBranch).
import { useEffect, useState } from 'react';
import { createBranch, updateBranch } from '../lib/branches';
import { getSignedLogoUrl, removeBranchLogo, uploadBranchLogo } from '../lib/branchLogos';
import { SCHEDULE_DAYS, defaultScheduleForm, scheduleToForm, formToSchedule } from '../lib/branchSchedule';

function emptyForm() {
  return {
    name: '',
    address: '',
    comuna: '',
    region: '',
    phone: '',
    orlEnabled: true,
    oftEnabled: true,
    mapsLinkEnabled: true,
    serviciosDescripcion: '',
    // Sucursal nueva: arranca con una plantilla editable, no vacía — ver
    // defaultScheduleForm() en src/lib/branchSchedule.js.
    schedule: defaultScheduleForm(),
  };
}

function branchToForm(branch) {
  return {
    name: branch.name ?? '',
    address: branch.address ?? '',
    comuna: branch.comuna ?? '',
    region: branch.region ?? '',
    phone: branch.phone ?? '',
    orlEnabled: branch.orl_enabled !== false,
    oftEnabled: branch.oft_enabled !== false,
    mapsLinkEnabled: branch.maps_link_enabled !== false,
    serviciosDescripcion: branch.servicios_descripcion ?? '',
    schedule: scheduleToForm(branch.schedule),
  };
}

export default function BranchFormModal({ branch, onClose, onSaved }) {
  const isEdit = !!branch;
  const [form, setForm] = useState(() => (isEdit ? branchToForm(branch) : emptyForm()));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Logo: `logoFile` es lo elegido en este paso (todavía no subido);
  // `logoPreviewUrl` es lo que se muestra (blob local del archivo elegido,
  // o la signed URL del logo ya guardado si se está editando).
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [logoError, setLogoError] = useState('');

  // BUG real encontrado en auditoría (23-ago-2026): el form siempre arranca
  // con `defaultScheduleForm()` cuando `branch.schedule` es `null` (todavía
  // sin configurar) — eso es solo para que el input no aparezca vacío/roto,
  // pero antes se guardaba ese template igual aunque el Admin solo hubiera
  // editado, por ejemplo, el teléfono. Resultado: CUALQUIER edición de una
  // sucursal "sin horario configurado" la sacaba silenciosamente del modo
  // fallback (9:00-17:45 fijo) y la dejaba con el horario de la plantilla,
  // sin que nadie lo haya pedido — contradice la garantía explícita de
  // DECISIONS.md #95 ("mientras no se guarde nada acá, sigue el horario
  // fijo de siempre"). `scheduleTouched` distingue "el Admin tocó algo acá"
  // de "esto es solo el valor de partida para mostrar algo en el form".
  const [scheduleTouched, setScheduleTouched] = useState(false);

  useEffect(() => {
    setForm(isEdit ? branchToForm(branch) : emptyForm());
    setLogoFile(null);
    setRemoveLogo(false);
    setLogoError('');
    setScheduleTouched(false);
    if (isEdit && branch.logo_path) {
      getSignedLogoUrl(branch.logo_path)
        .then(setLogoPreviewUrl)
        .catch(() => setLogoPreviewUrl(null));
    } else {
      setLogoPreviewUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  // Actualiza un solo día del horario sin pisar los otros 6 — `patch` es
  // parcial (ej. solo { enabled: false } al destildar un día).
  function setScheduleDay(dayKey, patch) {
    setScheduleTouched(true);
    setForm((f) => ({
      ...f,
      schedule: { ...f.schedule, [dayKey]: { ...f.schedule[dayKey], ...patch } },
    }));
  }

  function handleLogoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError('');
    setRemoveLogo(false);
    setLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
  }

  function handleRemoveLogo() {
    setLogoFile(null);
    setLogoPreviewUrl(null);
    setRemoveLogo(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }
    if (!form.orlEnabled && !form.oftEnabled) {
      setError('Debe quedar al menos un módulo habilitado (ORL u OFT).');
      return;
    }
    setSaving(true);
    try {
      // `form.schedule` está en la forma "de formulario" (booleanos enabled/
      // lunch) — se convierte a la forma jsonb que espera la base de datos
      // recién acá, al guardar (formToSchedule(), src/lib/branchSchedule.js).
      //
      // Si esta sucursal todavía no tenía horario configurado (`schedule`
      // null) y el Admin no tocó esa sección del formulario en esta pasada,
      // se guarda `null` de nuevo — no la plantilla que se muestra como
      // punto de partida — para no activar sin querer el horario limitado
      // solo por haber guardado un cambio en otro campo (ver comentario en
      // `scheduleTouched` más arriba).
      const scheduleWasConfigured = isEdit && branch?.schedule != null;
      const shouldSaveSchedule = !isEdit || scheduleWasConfigured || scheduleTouched;
      const payload = {
        ...form,
        schedule: shouldSaveSchedule ? formToSchedule(form.schedule) : null,
      };
      let branchId = branch?.id;
      if (isEdit) {
        await updateBranch(branch.id, payload);
      } else {
        branchId = await createBranch(payload);
      }

      // El logo se maneja aparte de nombre/dirección/teléfono porque la
      // ruta de Storage necesita el id de la sucursal, que recién existe
      // acá para el caso "nueva sucursal".
      if (logoFile) {
        try {
          await uploadBranchLogo(branchId, logoFile, isEdit ? branch.logo_path : null);
        } catch (logoErr) {
          setLogoError(logoErr.message || 'No se pudo subir el logo, pero la sucursal se guardó.');
        }
      } else if (removeLogo && isEdit && branch.logo_path) {
        try {
          await removeBranchLogo(branchId, branch.logo_path);
        } catch (logoErr) {
          setLogoError(logoErr.message || 'No se pudo quitar el logo.');
        }
      }

      onSaved();
    } catch (err) {
      if (err.code === '23505') {
        setError('Ya existe una sucursal activa con ese nombre.');
      } else {
        setError(err.message || 'No se pudo guardar la sucursal.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      {/* modal-card--wide (23-ago-2026): el horario semanal de abajo (7
          días × horas + almuerzo) no entra cómodo en los 420px por defecto
          de .modal-card — modificador propio, no toca el resto de los
          modales del software. */}
      <div className="modal-card modal-card--wide">
        <h2>{isEdit ? 'Editar sucursal' : 'Nueva sucursal'}</h2>

        <form onSubmit={handleSubmit} className="patient-form">
          <label className="field">
            <span>Nombre *</span>
            <input value={form.name} onChange={(e) => set('name', e.target.value)} required />
            <span className="field-hint">Se guarda en mayúsculas.</span>
          </label>

          <label className="field">
            <span>Dirección</span>
            <input value={form.address} onChange={(e) => set('address', e.target.value)} />
          </label>

          {/* Link automático de Maps (migración 0045, 24-ago-2026): la
              dirección de arriba se muestra como link a Google Maps en el
              correo de confirmación, el recordatorio y el comprobante
              impreso — salvo que se destilde acá. Pensado para sucursales
              itinerantes (ej. "Óptica Comunal Colina"), cuya ubicación real
              cambia y no debe comunicarse automáticamente. */}
          <label className="checkbox-inline" style={{ marginTop: -8 }}>
            <input
              type="checkbox"
              checked={form.mapsLinkEnabled}
              onChange={(e) => set('mapsLinkEnabled', e.target.checked)}
            />
            Generar link automático a Google Maps desde esta dirección
          </label>
          <span className="field-hint" style={{ marginTop: -8, marginBottom: 4 }}>
            Destildar solo si esta sucursal es itinerante o su dirección no es fija — el staff debe comunicar
            la ubicación real caso a caso.
          </span>

          {/* Comuna/región (migración 0042, 23-ago-2026): solo para la
              página pública nueva (PublicLanding.jsx) — a diferencia de
              `address`, NUNCA se usan en los documentos imprimibles. */}
          <div className="form-row">
            <label className="field">
              <span>Comuna</span>
              <input value={form.comuna} onChange={(e) => set('comuna', e.target.value)} />
            </label>
            <label className="field">
              <span>Región</span>
              <input value={form.region} onChange={(e) => set('region', e.target.value)} />
            </label>
          </div>
          <span className="field-hint" style={{ marginTop: -8, marginBottom: 4 }}>
            Comuna y región solo se muestran en la página pública (agendacomunal.cl) — no aparecen en
            comprobantes ni documentos imprimibles.
          </span>

          <label className="field">
            <span>Teléfono</span>
            <input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          </label>

          <div className="field">
            <span>Módulos habilitados</span>
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={form.orlEnabled}
                onChange={(e) => set('orlEnabled', e.target.checked)}
              />
              ORL (audiometrías, audífonos)
            </label>
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={form.oftEnabled}
                onChange={(e) => set('oftEnabled', e.target.checked)}
              />
              OFT (recetas de lentes, ventas)
            </label>
            <span className="field-hint">
              Un módulo deshabilitado deja de ofrecerse para agendar/registrar en esta sucursal — no borra lo
              que ya existe.
            </span>
          </div>

          {/* Horario semanal + almuerzo (migración 0043, 23-ago-2026): define
              qué horas ofrece el auto-agendamiento público para esta
              sucursal — cada una funciona distinto, por eso es por sucursal
              y no un horario único para todas. Mientras no se guarde nada
              acá, la Edge Function usa el horario fijo de siempre (9:00 a
              17:45 todos los días) — no rompe nada mientras Nicolás no lo
              cargue. */}
          <div className="field">
            <span>Horario de atención (auto-agendamiento)</span>
            {isEdit && branch?.schedule == null && !scheduleTouched && (
              <span className="field-hint">
                Esta sucursal todavía no tiene un horario propio guardado — hoy usa el horario fijo de
                siempre (9:00 a 17:45, todos los días, sin almuerzo). Lo de abajo es solo una plantilla de
                partida: si guardas sin tocarla, se mantiene el horario fijo; si la editas, se activa el
                horario que definas acá.
              </span>
            )}
            <div className="branch-schedule">
              {SCHEDULE_DAYS.map(({ key, label }) => {
                const day = form.schedule[key];
                return (
                  <div className="branch-schedule__day" key={key}>
                    <label className="branch-schedule__day-toggle">
                      <input
                        type="checkbox"
                        checked={day.enabled}
                        onChange={(e) => setScheduleDay(key, { enabled: e.target.checked })}
                      />
                      {label}
                    </label>
                    {day.enabled ? (
                      <>
                        <span className="branch-schedule__hours">
                          <input
                            type="time"
                            value={day.start}
                            onChange={(e) => setScheduleDay(key, { start: e.target.value })}
                          />
                          <span aria-hidden="true">–</span>
                          <input
                            type="time"
                            value={day.end}
                            onChange={(e) => setScheduleDay(key, { end: e.target.value })}
                          />
                        </span>
                        <label className="branch-schedule__lunch-toggle">
                          <input
                            type="checkbox"
                            checked={day.lunch}
                            onChange={(e) => setScheduleDay(key, { lunch: e.target.checked })}
                          />
                          Almuerzo
                        </label>
                        {day.lunch && (
                          <span className="branch-schedule__hours">
                            <input
                              type="time"
                              value={day.lunchStart}
                              onChange={(e) => setScheduleDay(key, { lunchStart: e.target.value })}
                            />
                            <span aria-hidden="true">–</span>
                            <input
                              type="time"
                              value={day.lunchEnd}
                              onChange={(e) => setScheduleDay(key, { lunchEnd: e.target.value })}
                            />
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="branch-schedule__closed">Cerrado</span>
                    )}
                  </div>
                );
              })}
            </div>
            <span className="field-hint">
              Define qué horas ofrece el auto-agendamiento público (/agendamiento-online) para esta sucursal
              — no afecta el agendamiento manual desde la Agenda interna.
            </span>
          </div>

          <label className="field">
            <span>Descripción de servicios (página pública)</span>
            <textarea
              rows={3}
              value={form.serviciosDescripcion}
              onChange={(e) => set('serviciosDescripcion', e.target.value)}
              placeholder="Ej: Audiometrías, adaptación de audífonos, control auditivo."
            />
            <span className="field-hint">
              Se muestra tal cual en la página pública de agendacomunal.cl, junto al nombre y dirección
              de esta sucursal. Opcional — si queda vacío, esa sucursal simplemente no muestra esa línea.
            </span>
          </label>

          {/* `div`, no `label` — a diferencia de los otros campos, este tiene un
              botón ("Quitar logo") además del input; si todo quedara envuelto en
              un <label> el clic en el botón dispararía también un clic sintético
              en el input de archivo (el navegador reenvía el clic del label a su
              control asociado). */}
          <div className="field">
            <span>Logo / imagen de sucursal</span>
            {logoPreviewUrl && (
              <img
                src={logoPreviewUrl}
                alt="Logo de la sucursal"
                style={{ maxHeight: 64, maxWidth: 160, objectFit: 'contain', marginBottom: 6, display: 'block' }}
              />
            )}
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoChange} />
            <span className="field-hint">Se usa en el membrete de los documentos impresos. Máximo 2 MB.</span>
            {logoPreviewUrl && (
              <button type="button" className="link-button link-button--danger" onClick={handleRemoveLogo} style={{ marginTop: 4 }}>
                Quitar logo
              </button>
            )}
            {logoError && <span className="field-hint" style={{ color: 'var(--danger, #c0392b)' }}>{logoError}</span>}
          </div>

          {error && <p className="auth-error">{error}</p>}

          <div className="modal-actions">
            <button type="button" className="button-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="button-primary" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
